// src/pages/healthcare/appointments/components/UnavailableBlockCard.tsx
import React from "react";
import { Tooltip } from "antd";
import { CoffeeOutlined, FileTextOutlined, LockOutlined, ReadOutlined, StopOutlined, SunOutlined, TeamOutlined } from "@ant-design/icons";
import { TeamMember } from "../../../../types/admin/TeamTypes";
import PractitionerAvatar from "../../../../components/shared/PractitionerAvatar";
import { BlockType, UnavailableSource } from "../../../../utils/appointments/availability";

export type BlockKind = UnavailableSource | "day-off";

export interface BlockVisual {
	kind: BlockKind;
	blockType: BlockType;
	reason: string | null;
}

const TYPE_LABELS: Record<BlockType, string> = {
	lunch: "Lunch",
	admin: "Admin",
	meeting: "Meeting",
	training: "Training",
	leave: "Leave",
	other: "Unavailable",
};

const TYPE_ICONS: Record<BlockType, React.ReactNode> = {
	lunch: <CoffeeOutlined />,
	admin: <FileTextOutlined />,
	meeting: <TeamOutlined />,
	training: <ReadOutlined />,
	leave: <SunOutlined />,
	other: <StopOutlined />,
};

export const blockLabel = ({ kind, blockType, reason }: BlockVisual): string => {
	const head = kind === "closure" ? "Clinic closed" : kind === "day-off" ? "Unavailable all day" : TYPE_LABELS[blockType];

	return reason ? `${head} — ${reason}` : head;
};

export const blockClassName = ({ kind, blockType }: BlockVisual): string => `appointments-block appointments-block--${kind === "block" ? blockType : kind}`;

const iconFor = ({ kind, blockType }: BlockVisual): React.ReactNode => (kind === "closure" ? <LockOutlined /> : kind === "day-off" ? <StopOutlined /> : TYPE_ICONS[blockType]);

// The label is clipped, never ellipsised into nonsense — a block too short to fit it reads on hover.
// `member` is set only for the all-staff week's cards, which have no column to say whose block it is.
const UnavailableBlockCard: React.FC<{ visual: BlockVisual; member?: TeamMember | null }> = ({ visual, member = null }) => {
	const label = blockLabel(visual);
	const name = member ? `${member.first_name} ${member.last_name}`.trim() : null;

	return (
		<Tooltip title={name ? `${name} · ${label}` : label}>
			<span className="appointments-block-body" data-testid={`appointments-block-${visual.kind}`}>
				<span className="appointments-block-icon">{iconFor(visual)}</span>
				<span className="appointments-block-label">{label}</span>
				{name && (
					<span className="appointments-block-prac">
						<PractitionerAvatar member={member} size={14} />
						{name}
					</span>
				)}
			</span>
		</Tooltip>
	);
};

export default UnavailableBlockCard;
